import React, { forwardRef } from "react";
import { string, node, bool, func } from "prop-types";
import { motion, AnimatePresence } from "framer-motion";
import styled, { useTheme } from "styled-components";

import { usePrimaryColor } from "@hooks/usePrimaryColor";

import AnimatedArrow from "./Icons/AnimatedArrow";
import Typography from "./Typography";
import { font, fontSize, fontWeight } from "./utils";

const ContentContainer = styled.div`
  overflow-y: hidden;
`;
const Content = styled.div`
  padding: 1rem;
`;

const Header = styled(motion.div)`
  cursor: pointer;
  color: white;
  min-height: 2rem;
  padding: 0 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const AccordionWrapper = styled.div`
  padding: 0 1.1rem;
`;

const StyledText = styled(Typography)`
  color: inherit;
  margin: 0.5rem 0;
  text-transform: uppercase;
  font-weight: ${fontWeight("bolder")};
  font-size: ${fontSize("md")};
  font-family: ${font("citroen")};
`;

const SmallAccordion = forwardRef(
  (
    { title, className, children, isOpen, setIsOpen, disabled, customColor },
    ref
  ) => {
    const toggleOpen = () => {
      setIsOpen(!isOpen);
    };

    const theme = useTheme();
    const primaryColor = usePrimaryColor();

    const color = customColor ?? primaryColor;

    return (
      <AccordionWrapper ref={ref} className={className}>
        <Header
          data-id="small-accordion"
          onClick={!disabled && toggleOpen}
          initial={{
            color: isOpen ? color : theme.colors.white,
            backgroundColor: !isOpen ? color : theme.colors.white,
          }}
          animate={{
            color: isOpen ? color : theme.colors.white,
            backgroundColor: !isOpen ? color : theme.colors.white,
          }}
        >
          <StyledText>{title}</StyledText>
          <AnimatedArrow
            size="default"
            down={!isOpen}
            up={isOpen}
            data-id={`collapsable-item-arrow-${title}-${
              isOpen ? "up" : "down"
            }`}
          />
        </Header>
        <ContentContainer>
          <AnimatePresence initial={false}>
            {isOpen && (
              <motion.div
                initial={{
                  height: 0,
                  display: "none",
                }}
                animate={{
                  height: "auto",
                  display: "block",
                }}
                exit={{
                  height: 0,
                  transitionEnd: { display: "none" },
                }}
              >
                <Content>{children}</Content>
              </motion.div>
            )}
          </AnimatePresence>
        </ContentContainer>
      </AccordionWrapper>
    );
  }
);

SmallAccordion.displayName = "SmallAccordion";

SmallAccordion.propTypes = {
  children: node,
  className: string,
  isOpen: bool,
  disabled: bool,
  setIsOpen: func,
  title: string,
  customColor: string,
};

export default SmallAccordion;
